/**
 * Sandbox-side mirror of the DesignSpec data model.
 *
 * Why a mirror instead of importing from `@gotti/sandbox-ai`?
 *
 * The sandbox UI runs in a browser and shouldn't pull in the full
 * sandbox-ai bundle (LangChain, embedders, the tool registry, etc.)
 * just to render a table of specs. We mirror the data shape here so
 * the UI stays light. The frontmatter format is identical so specs
 * round-trip between this code and the agent's tools.
 *
 * If a field is added on the agent side, mirror it here — and keep
 * `parseSpec` / `serializeSpec` in lock-step.
 */

export type DesignSpecCategory =
  | 'overview'
  | 'mechanic'
  | 'system-contract'
  | 'decision'
  | 'open-question';

export const DESIGN_SPEC_CATEGORIES: readonly DesignSpecCategory[] = [
  'overview',
  'mechanic',
  'system-contract',
  'decision',
  'open-question',
] as const;

export const ARCHIVE_SENTINEL_SPEC_ID = 'spec_00000000';

export interface CodeSymbolRef {
  path: string;
  symbol?: string;
}

export interface DesignSpec {
  id: string;
  title: string;
  category: DesignSpecCategory;
  systemName?: string;
  relatedCodeSymbols: CodeSymbolRef[];
  relatedSpecIds: string[];
  supersedes?: string;
  supersededBy?: string;
  createdAt: string;
  updatedAt: string;
  body: string;
}

/** Display labels for each category. */
export const CATEGORY_LABEL: Record<DesignSpecCategory, string> = {
  'overview':         'Overview',
  'mechanic':         'Mechanics',
  'system-contract':  'System Contracts',
  'decision':         'Decisions',
  'open-question':    'Open Questions',
};

/** Short description shown in empty states. */
export const CATEGORY_BLURB: Record<DesignSpecCategory, string> = {
  'overview':         'Game-level facts: genre, win/lose, target feel.',
  'mechanic':         'Rules, controls, scoring, tunable numbers.',
  'system-contract':  "A system's responsibilities, owned state, and message contract.",
  'decision':         'Append-only log of recorded design choices.',
  'open-question':    'Questions the design has not yet resolved.',
};
